/**
 * Precision CAP Portal — top-level shell.
 *
 * Mounted by App.js when the request hostname starts with "cap-portal." (or
 * for local dev when the path begins with /_portal). Owns its own router
 * (independent of Core's router) and its own session state. The session
 * here is completely separate from Core's auth — it lives under
 * /api/portal/* and only carries an external_user identity bound to
 * exactly one CAP.
 *
 * Routes:
 *   /redeem/:token    — first-time invitation redemption
 *   /login            — sign-in
 *   /mfa/enroll       — first-time MFA setup
 *   /mfa/challenge    — MFA at every subsequent sign-in
 *   /cap              — the one CAP this user can see
 *   /                 — bounce: authenticated → /cap, otherwise → /login
 *
 * Local-dev path-prefix support: when running on localhost the React app is
 * served from a single origin, and we mount the portal under /_portal/* so
 * routes look like /_portal/login. Production uses the cap-portal subdomain
 * and routes are clean (no prefix).
 */

import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';

import PortalLogin from './PortalLogin';
import PortalRedeem from './PortalRedeem';
import PortalMfaEnroll from './PortalMfaEnroll';
import PortalMfaChallenge from './PortalMfaChallenge';
import PortalCapView from './PortalCapView';

const API = process.env.REACT_APP_API_URL || '';

function isLocalPathPrefix() {
  if (typeof window === 'undefined') return false;
  return (
    window.location.hostname === 'localhost' &&
    window.location.pathname.startsWith('/_portal')
  );
}

function portalBasename() {
  return isLocalPathPrefix() ? '/_portal' : '/';
}

function RequireAuth({ user, sessionLoaded, children }) {
  if (!sessionLoaded) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RootRedirect({ user, sessionLoaded }) {
  if (!sessionLoaded) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  return <Navigate to={user ? '/cap' : '/login'} replace />;
}

function CapRoute({ user, onSessionExpired }) {
  return <PortalCapView user={user} onSessionExpired={onSessionExpired} />;
}

function PortalRoutes({ user, sessionLoaded, onSessionExpired }) {
  // useNavigate is only valid inside a Router subtree, so this component
  // exists to give us a navigation handle for the auth-expired callback.
  const navigate = useNavigate();
  const handleExpired = () => {
    if (onSessionExpired) onSessionExpired();
    navigate('/login', { replace: true });
  };

  return (
    <Routes>
      <Route path="/redeem/:token" element={<PortalRedeem />} />
      <Route path="/login" element={<PortalLogin />} />
      <Route path="/mfa/enroll" element={<PortalMfaEnroll />} />
      <Route path="/mfa/challenge" element={<PortalMfaChallenge />} />
      <Route
        path="/cap"
        element={
          <RequireAuth user={user} sessionLoaded={sessionLoaded}>
            <CapRoute user={user} onSessionExpired={handleExpired} />
          </RequireAuth>
        }
      />
      <Route
        path="/"
        element={<RootRedirect user={user} sessionLoaded={sessionLoaded} />}
      />
      {/* Anything we don't recognize bounces to / — there are no other pages. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function PortalApp() {
  const [user, setUser] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  const refreshSession = async () => {
    try {
      const res = await fetch(`${API}/api/portal/session`, {
        method: 'GET',
        credentials: 'include',
      });
      if (res.status === 401) {
        setUser(null);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.authenticated && data.external_user) {
        setUser(data.external_user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setSessionLoaded(true);
    }
  };

  useEffect(() => {
    refreshSession();
  }, []);

  const handleSessionExpired = () => {
    setUser(null);
  };

  return (
    <BrowserRouter basename={portalBasename()}>
      <PortalRoutes
        user={user}
        sessionLoaded={sessionLoaded}
        onSessionExpired={handleSessionExpired}
      />
    </BrowserRouter>
  );
}
